<template>
  <div class="category-picker" :class="{ 'opacity-0': !overflowBehaviorHasBeenTested }">
    <template v-if="type === 'chips' && dialogOnChipOverflow && chipsOverflows">
      <v-chip
        class="ml-3"
        :close="!!selectedCategories"
        :color="!!selectedCategories ? 'secondary' : 'white'"
        :text-color="!!selectedCategories ? 'white' : 'primary darken-2'"
        @click:close="unselectAll"
        @click="showDialog = true"
      >
        <template v-if="!!selectedCategories">
          {{ chipSelectionText }}
        </template>
        <template v-else>
          <v-icon class="mr-2" small>mdi-filter</v-icon>
          Filtrera på platskategori
        </template>
      </v-chip>
      <v-dialog
        v-model="showDialog"
        max-width="450"
      >
        <v-card>
          <v-card-title class="">
            Välj platskategori
          </v-card-title>
          <v-card-text style="max-height: 60vh; overflow-y: auto;">
            <v-list dense>
              <v-list-item
                v-for="selectable in selectables.filter(selectable => selectable.model === 'lodging_type')"
                :key="selectable.key"
                @click="selectedKeys = selectable.key; showDialog = false"
                class="soft-border"
              >
                <v-list-item-icon>
                  <v-icon>
                    {{ selectable.icon }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ selectable.name }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <template v-if="selectables.findIndex(selectable => selectable.model === 'lodging_type') > -1">
                <v-divider class="my-2"></v-divider>
              </template>
              <v-list-item
                v-for="selectable in selectables.filter(selectable => selectable.model === 'category')"
                :key="selectable.key"
                @click="selectedKeys = selectable.key; showDialog = false"
                class=""
              >
                <v-list-item-icon>
                  <v-icon>
                    {{ selectable.icon }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ selectable.name }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-dialog>

    </template>
    <template v-else-if="type === 'chips'">
      <v-chip-group
        ref="chipGroup"
        v-model="selectedKeys"
        @change="onInput"
        center-active
        show-arrows
        :multiple="selectMultiple"
        color="secondary"
      >
        <v-chip
          v-for="selectable in selectables.filter(selectable => selectable.model === 'lodging_type')"
          :key="selectable.key"
          :value="selectable.key"
          :color="chipColor"
          class="soft-border"
        >
          <v-icon left>
            {{ selectable.icon }}
          </v-icon>
          {{ selectable.name }}
        </v-chip>
        <template v-if="selectables.findIndex(selectable => selectable.model === 'lodging_type') > -1">
          <v-divider vertical class="ml-1 mr-3"></v-divider>
        </template>
        <v-chip
          v-for="selectable in selectables.filter(selectable => selectable.model === 'category')"
          :key="selectable.key"
          :value="selectable.key"
          :color="chipColor"
          class="soft-border"
        >
          <v-icon left>
            {{ selectable.icon }}
          </v-icon>
          {{ selectable.name }}
        </v-chip>
      </v-chip-group>
    </template>
    <template v-else-if="type === 'dropdown'">
      <category-select
        v-model="selectedKeys"
        :items="selectables"
        @change="onInput"
        :multiple="selectMultiple"
        hide-details
        outlined
        :dense="dense"
        clearable
        item-value="key"
        :label="label"
        style="margin-top: 1px;"
        :menu-props="{ maxHeight: '60vh' }"
      >
        <template v-slot:selection="{ item, index }">
          <v-chip v-if="index === 0" small color="secondary">
            <span>{{ item.name }}</span>
          </v-chip>
          <span
            v-if="index === 1"
            class="grey--text text-caption"
          >
            (+{{ value.length - 1 }} till)
          </span>
        </template>
      </category-select>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CategorySelect from '@/components/position/CategorySelect.vue'

export default {
  name: 'CategoryPicker',
  components: { CategorySelect },
  props: {
    value: [Object, Array, Number], // Array om selectMultiple är true, annars Number of returnIds är true, annars Object
    chipColor: {
      type: String,
      default: 'white'
    },
    selectMultiple: Boolean,
    columnOnMobile: Boolean,
    includeLodgingTypes: Boolean, // Fungerar bara tillsammans med selectMultiple
    returnIds: Boolean, // Om true returnerar id:n istället för hela objektet
    dialogOnChipOverflow: {
      type: Boolean, // Fungerar tillsammans med type='chips'. Om det är så många chips att chip-group overflowar så visar den bara ett chip som man kan klicka på för att visa alla alternativ i en dialog istället
      default: false // Fungerar just nu inte med selectMultiple
    },
    type: {
      // 'chips' eller 'dropdown'
      type: String,
      default: 'chips'
    },
    label: {
      type: String,
      default: 'Filtrera på platskategori'
    },
    dense: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    selectedKeys: [],
    isUserInput: false,
    chipGroupWatcherInterval: null,
    chipsOverflows: false,
    showDialog: false, // Dialog för att välja kategori om chips overflowar
    overflowBehaviorHasBeenTested: false // Om observeOverflowingBehavior har körts minst en gång
  }),
  computed: {
    ...mapGetters(
      {
        categories: 'position/activeCategories',
        activeLodgingTypes: 'position/activeLodgingTypes'
      }
    ),
    selectables: function () {
      let arr = []
      if (this.includeLodgingTypes && this.selectMultiple && this.activeLodgingTypes.length > 1) {
        // Visar också lodging types om använder include-lodging-types och campingen har mer än 1 lodging type
        arr = this.activeLodgingTypes.concat(this.categories)
      } else {
        arr = this.categories
      }
      arr = arr.map(selectable => {
        return {
          id: selectable.id,
          key: (selectable.icon_class_name ? 'category' : 'lodging_type') + `_${selectable.id}`,
          model: selectable.icon_class_name ? 'category' : 'lodging_type',
          name: selectable.name || selectable.name_sv,
          icon: selectable.icon_class_name || selectable.lodging_type_icon
        }
      })
      if (this.includeLodgingTypes && this.selectMultiple && this.activeLodgingTypes.length > 1 && this.type === 'dropdown') {
        // Lägger in en divider efter lodging types
        const lodgingTypeIndex = this.activeLodgingTypes.length
        arr.splice(lodgingTypeIndex, 0, { divider: true })
      }
      return arr
    },
    selectedCategories: function () {
      if (!this.selectedKeys || this.selectedKeys.length === 0) {
        return this.selectMultiple ? [] : null
      }
      if (this.selectMultiple) {
        const categoryIds = this.selectedKeys.filter(key => key.includes('category')).map(key => parseInt(key.split('_')[1], 10))
        const categories = this.categories.filter(category => categoryIds.includes(category.id))
        return categories
      } else {
        const categoryId = parseInt(this.selectedKeys.split('_')[1], 10)
        const category = this.categories.find(category => categoryId === category.id)
        return category
      }
    },
    chipSelectionText: function () {
      // Används endast när dialogOnChipOverflow är true och chips overflowar
      // Inte stöd för detta vid selectMultiple så behöver bara vissa en kategori
      const category = this.selectedCategories
      if (!category) {
        return ''
      }
      return category.name
    }
  },
  methods: {
    onInput: function (val) {
      this.isUserInput = true
    },
    observeOverflowingBehavior: function () {
      if (!this.dialogOnChipOverflow || this.type !== 'chips') {
        this.overflowBehaviorHasBeenTested = true
        return false
      }
      this.chipGroupWatcherInterval = setInterval(() => {
        const hasRef = !!this.$refs.chipGroup
        if (!hasRef) {
          return
        }
        const chipsOverflows = this.$refs.chipGroup.hasNext || this.$refs.chipGroup.hasPrev
        console.log('chipsOverflows', chipsOverflows)
        clearInterval(this.chipGroupWatcherInterval)
        this.chipsOverflows = chipsOverflows
        this.overflowBehaviorHasBeenTested = true
      }, 100)
    },
    toggleActiveLodgingTypes: function () {
      if (!this.selectedKeys) {
        return false
      }
      // om alla kategorierna för en lodging type är aktiverade, aktivera även den lodging typen
      // om någon kategori för en lodging type är avaktiverad, avaktivera även den lodging typen
      this.activeLodgingTypes.forEach(lodgingType => {
        const lodgingTypeCategories = this.categories.filter(category => category.lodging_type_ids.includes(lodgingType.id))
        const isAllActive = lodgingTypeCategories.every(lodgingTypeCategory => this.selectedKeys.includes(`category_${lodgingTypeCategory.id}`))
        if (isAllActive) {
          if (!this.selectedKeys.includes(`lodging_type_${lodgingType.id}`)) {
            // Lägger inte till dubbletter
            this.selectedKeys.push(`lodging_type_${lodgingType.id}`)
          }
        } else {
          this.selectedKeys = this.selectedKeys.filter(key => key !== `lodging_type_${lodgingType.id}`)
        }
      })
    },
    toggleActiveCategories: function () {
      // om någon lodging type för en kategori är aktiverad, aktivera även kategorin
      // om ingen lodging type för en kategori är aktiverad, avaktivera kategorin
      this.categories.forEach(category => {
        const categoryLodgingTypes = category.lodgingTypes
        const isAnyActive = categoryLodgingTypes.some(categoryLodgingType => this.selectedKeys.includes(`lodging_type_${categoryLodgingType.id}`))
        if (isAnyActive) {
          if (!this.selectedKeys.includes(`category_${category.id}`)) {
            // Lägger inte till dubbletter
            this.selectedKeys.push(`category_${category.id}`)
          }
        } else {
          this.selectedKeys = this.selectedKeys.filter(key => key !== `category_${category.id}`)
        }
      })
    },
    updateSelectedKeys: function () {
      // Uppdaterar selectedKeys baserat på value
      if (!this.selectMultiple) {
        if (this.returnIds) {
          // Returnerar id:n istället för hela objektet
          this.selectedKeys = this.value ? `category_${this.value}` : null
        } else {
          this.selectedKeys = this.value ? `category_${this.value.id}` : null
        }
      } else {
        let ids = []
        if (Array.isArray(this.value)) {
          if (this.returnIds) {
            ids = this.value
          } else {
            ids = this.value.map(category => category.id)
          }
        } else if (this.value) {
          if (this.returnIds) {
            ids = [this.value]
          } else {
            ids = [this.value.id]
          }
        } else {
          ids = []
        }
        this.selectedKeys = this.selectedKeys.filter(key => key.includes('lodging_type')).concat(ids.map(id => `category_${id}`))
      }
    },
    unselectAll: function () {
      this.isUserInput = true
      this.selectedKeys = this.selectMultiple ? [] : null
    }
  },
  watch: {
    value: function (newVal, oldVal) {
      if (this.selectMultiple && Array.isArray(newVal) && Array.isArray(oldVal)) {
        // Om selectMultiple är true och både newVal och oldVal är arrayer, jämför dem
        if (newVal.length === oldVal.length && newVal.every((val, index) => val.id === oldVal[index].id)) {
          return // Ingen ändring
        }
      } else if (newVal === oldVal) {
        return // Ingen ändring
      }
      this.updateSelectedKeys()
    },
    selectedKeys: function (newVal, oldVal) {
      if (!this.selectMultiple) {
        // selectedKeys är alltid string om selectMultiple är false, så behöver inte behandlingen nedan
        return false
      }
      // console.log('newVal', newVal, 'oldVal', oldVal)
      if (!this.isUserInput) {
        // Kör inte denna metod om det inte är användaren som har ändrat selectedKeys, kan bli oändlig loop
        return false
      }
      this.isUserInput = false

      const addedSelectableKey = newVal.find(x => !oldVal.includes(x))
      const removedSelectableKey = oldVal.find(x => !newVal.includes(x))
      if (!addedSelectableKey && !removedSelectableKey) {
        // Ingen ändring
        return false
      }

      if (addedSelectableKey?.includes('lodging_type') || removedSelectableKey?.includes('lodging_type')) {
        // Aktiverat eller avaktiverat en lodging type, kontrollerar om några kategorier ska aktiveras eller avaktiveras
        this.toggleActiveCategories()
      } else if (addedSelectableKey?.includes('category') || removedSelectableKey?.includes('category')) {
        // Aktiverat eller avaktiverat en kategori, kontrollerar om några lodging types ska aktiveras eller avaktiveras
        this.toggleActiveLodgingTypes()
      }
    },
    selectedCategories: function () {
      if (this.returnIds) {
        // Returnerar id:n istället för hela objektet
        if (!this.selectMultiple) {
          this.$emit('input', this.selectedCategories?.id)
        } else {
          this.$emit('input', this.selectedCategories.map(category => category.id))
        }
      } else {
        this.$emit('input', this.selectedCategories)
      }
    },
    selectables: function () {
      this.observeOverflowingBehavior()
    },
    type: function () {
      this.observeOverflowingBehavior()
    }
  },
  mounted () {
    this.updateSelectedKeys()
    this.observeOverflowingBehavior()
    this.toggleActiveLodgingTypes()
  },
  beforeDestroy () {
    if (this.chipGroupWatcherInterval) {
      clearInterval(this.chipGroupWatcherInterval)
    }
  }
}
</script>
